import React, { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { Button } from 'primereact/button';
import { Avatar } from 'primereact/avatar';
import 'primereact/resources/primereact.min.css';
import { SelectedRow } from '../../salaries-generator-page';
import { toast } from 'react-toastify';
import { paySalaryForProfessorOperation } from './operations/pay-salary-for-professor.operation';
import globalSalaryStore from '../../../../../stores/accountant/global-salary-store';
import { UserPageDialog } from '../../../../../component/ui/search-users/user-page-dialog/user-page-dialog';
import { UserType } from '../../../../../api/types/user/user-type';
import { UserSummaryType } from '../../../../../api/types/user/user-summary.type';

interface SalariesSummeryProps {
  selectedRows: SelectedRow[];
  onPaymentSuccess?: () => void;
}

const SummaryContainer = styled('div')({
  width: '350px',
  padding: '20px',
  backgroundColor: '#fff',
  borderRadius: '12px',
  boxShadow: '0 4px 6px rgba(0,0,0,0.05), 0 1px 3px rgba(0,0,0,0.1)',
  direction: 'rtl',
  fontFamily: "'Segoe UI', 'Arial', sans-serif",
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: '0 6px 8px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.12)',
    transform: 'translateY(-1px)',
  },
});

const SummaryRow = styled('div')({
  display: 'grid',
  gridTemplateColumns: '2fr 1fr 1fr 1fr',
  gap: '16px',
  padding: '12px 8px',
  margin: '4px 0',
  borderBottom: '1px solid #eaeaea',
  fontSize: '14px',
  alignItems: 'center',
  borderRadius: '6px',
  transition: 'all 0.15s ease-in-out',
  '&:last-child': {
    borderBottom: 'none',
  },
  '&:hover': {
    backgroundColor: '#f8f9fa',
    transform: 'scale(1.01)',
  },
  '& > div': {
    textAlign: 'start',
    padding: '4px 8px',
    '&:not(:first-of-type)': {
      textAlign: 'center',
    },
  },
});

const HeaderRow = styled(SummaryRow)({
  fontWeight: '600',
  borderBottom: '2px solid #e0e0e0',
  color: '#444',
  fontSize: '13px',
  textTransform: 'none',
  padding: '8px',
  marginBottom: '8px',
  backgroundColor: '#fafafa',
  '&:hover': {
    transform: 'none',
    backgroundColor: '#fafafa',
  },
});

const ValueCell = styled('div')({
  color: '#2c3e50',
  transition: 'color 0.2s ease',
  '&:hover': {
    color: '#34495e',
  },
});

const RateCell = styled('div')({
  color: '#2980b9',
  fontWeight: '500',
  transition: 'color 0.2s ease',
  '&:hover': {
    color: '#3498db',
  },
});

const TotalCell = styled('div')({
  color: '#16a34a',
  fontWeight: '500',
  transition: 'color 0.2s ease',
  borderRadius: '4px',
  '&:hover': {
    color: '#22c55e',
  },
});

const ActivityTypeCell = styled('div')({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  transition: 'color 0.2s ease',
  '&:hover': {
    color: '#1e293b',
  },
});

const TotalSection = styled('div')({
  marginTop: '20px',
  paddingTop: '16px',
  borderTop: '2px solid #e0e0e0',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

const GrandTotal = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontSize: '16px',
  fontWeight: '600',
  color: '#16a34a',
  padding: '0 8px',
});

const LecturerHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  marginBottom: '20px',
  padding: '0 8px 16px',
  borderBottom: '2px solid #e0e0e0',
});

const LecturerInfo = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

const LecturerName = styled('div')({
  fontSize: '18px',
  fontWeight: '600',
  color: '#1e293b',
});

const LecturerId = styled('div')({
  fontSize: '14px',
  color: '#64748b',
});

export const SalariesSummery: React.FC<SalariesSummeryProps> = ({
  selectedRows,
  onPaymentSuccess,
}) => {
  const user = globalSalaryStore.user;
  const previousUserIdRef = useRef<number | null>(null);

  // Clear data when user changes
  useEffect(() => {
    const currentUserId = user?.id || null;

    // If user changed (and it's not the initial load with no user)
    if (
      previousUserIdRef.current !== null &&
      previousUserIdRef.current !== currentUserId
    ) {
      // Clear the selected working hours data
      globalSalaryStore.selectedWorkingHourToPay = [];
    }

    // Update the previous user ID reference
    previousUserIdRef.current = currentUserId;
  }, [user?.id]);

  const grandTotal = selectedRows.reduce((sum, row) => sum + row.total, 0);
  const [loading, setLoading] = useState(false);
  const handlePay = async () => {
    const success = await paySalaryForProfessorOperation(
      user?.id ?? null,
      globalSalaryStore.selectedWorkingHourToPay.map((item) => ({
        workingHourId: item.workingHourId,
        rateId: item.rateId,
      })),
      setLoading,
    );

    // If payment was successful, trigger the callback
    if (success && onPaymentSuccess) {
      onPaymentSuccess();
    }
  };

  const [selectedUserForDialog, setSelectedUserForDialog] = useState<
    UserSummaryType | undefined
  >();

  const closeUserDialog = () => {
    setSelectedUserForDialog(undefined);
  };

  return (
    <SummaryContainer>
      <LecturerHeader onClick={() => user && setSelectedUserForDialog(user)}>
        <Avatar
          image={user?.fileUrl}
          size="large"
          shape="circle"
          style={{ width: '60px', height: '60px' }}
        />
        <LecturerInfo>
          <LecturerName>{`${user?.firstName} ${user?.lastName}`}</LecturerName>
          <LecturerId>ת.ז: {user?.nationalId}</LecturerId>
        </LecturerInfo>
      </LecturerHeader>

      <HeaderRow>
        <div>סוג פעילות</div>
        <div>תעריף (60 דק)</div>
        <div>כמות</div>
        <div>סה״כ</div>
      </HeaderRow>
      {selectedRows.map((row, index) => (
        <SummaryRow key={index}>
          <ActivityTypeCell>{row.course}</ActivityTypeCell>
          <RateCell>₪{row.rate}</RateCell>
          <ValueCell>{row.quantity}</ValueCell>
          <TotalCell>₪{row.total}</TotalCell>
        </SummaryRow>
      ))}
      <TotalSection>
        <GrandTotal>
          <span>סה״כ לתשלום:</span>
          <span>₪{grandTotal}</span>
        </GrandTotal>
        <Button
          label="אשר תשלום"
          severity="success"
          raised
          onClick={handlePay}
          style={{
            direction: 'rtl',
            width: '100%',
          }}
        />
      </TotalSection>

      <UserPageDialog
        userRole={UserType.LECTURER}
        selectedUser={selectedUserForDialog}
        setSelectedUser={setSelectedUserForDialog}
        closeDialog={closeUserDialog}
        onHide={closeUserDialog}
      />
    </SummaryContainer>
  );
};
